/**
 * V2 admin · Directory.
 *
 * Everyone with a login — super admins, admins, tenants — and the role tiers
 * that say what each of them can reach.
 *
 * This used to be the middle tab on Owners & access. It moved out because it
 * was never an owner screen: owners are deliberately excluded from it (they
 * have their own tab there), so a person looking for a tenant's account had to
 * open a page named after owners to find it. It now sits with the other tools
 * an admin visits occasionally, behind "More".
 *
 * Nothing about who can see what changed. The two gates the tab carried are
 * carried here unchanged:
 *
 *   - hasStaffRole (super_admin | tech | admin) → the role tier cards
 *   - super_admin | admin                       → the full user-roles table
 *
 * Collapsing them into one gate would quietly take the tier cards away from
 * tech accounts.
 */
import { Navigate } from "react-router-dom";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import {
  AdminRoleManagementPanel,
  AdminRoleTierOverview,
} from "@/components/admin/AdminRolePanels";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

export default function V2DirectoryPage() {
  const { isOwnerPortalOnly, isSuperAdmin, hasSalesAdminRole, hasStaffRole } =
    useAuth();
  const canManageAccess = isSuperAdmin || hasSalesAdminRole;
  const { t } = useLanguage();

  // Owner-portal accounts never had this tab, and /admin/owners bounces them
  // to their properties. Same destination here, so the new address can't become
  // a way in that the old one wasn't.
  if (isOwnerPortalOnly) return <Navigate to="/admin/properties" replace />;
  // A console account without a staff role saw no Directory tab at all — the
  // page is the tab, so it stays that way.
  if (!hasStaffRole) return <Navigate to="/admin/owners" replace />;

  return (
    <AdminV2Layout title={t("admin.nav.directory", "Directory")}>
      <div className="space-y-4">
        <p className="max-w-3xl text-sm text-muted-foreground">
          {t(
            "admin.directory.subtitle",
            "Everyone with a login and what their role can reach. Owners are a role too, but they live on Owners & access."
          )}
        </p>

        {canManageAccess ? (
          <AdminRoleManagementPanel excludeTiers={["owner"]} />
        ) : (
          // Tech accounts could always see the tier breakdown but never the
          // directory itself. Keep exactly that.
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">
                {t("admin.directory.roles.heading", "Roles")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(
                  "admin.directory.roles.subtitle",
                  "Staff tiers, with progressively narrower permissions."
                )}
              </p>
            </div>
            <AdminRoleTierOverview excludeTiers={["owner"]} />
          </div>
        )}
      </div>
    </AdminV2Layout>
  );
}
